import React, { useState, useEffect } from 'react';
import { 
  UserGroupIcon, 
  BuildingOfficeIcon,
  ArrowRightIcon,
  XMarkIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import api from '../../services/api';

interface Merchant {
  id: number;
  full_name: string;
  email: string;
  business_name: string;
  merchant_code: string;
  dispatchers_count: number;
  approval_status: string;
}

interface Dispatcher {
  id: number;
  full_name: string;
  email: string;
  phone: string;
  vehicle_type: string | null;
  approval_status: string;
  is_available: boolean;
  current_merchant?: {
    id: number;
    business_name: string;
    merchant_code: string;
  } | null;
  assignment_type?: 'self_registered' | 'admin_assigned' | null;
}

const AdminDispatcherAssignment: React.FC = () => {
  const [merchants, setMerchants] = useState<Merchant[]>([]);
  const [unassignedDispatchers, setUnassignedDispatchers] = useState<Dispatcher[]>([]);
  const [assignedDispatchers, setAssignedDispatchers] = useState<Dispatcher[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedMerchant, setSelectedMerchant] = useState<Merchant | null>(null);
  const [selectedDispatchers, setSelectedDispatchers] = useState<number[]>([]);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [merchantSearch, setMerchantSearch] = useState('');
  const [dispatcherSearch, setDispatcherSearch] = useState('');
  const [viewMode, setViewMode] = useState<'unassigned' | 'assigned'>('unassigned');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [merchantsRes, unassignedRes] = await Promise.all([
        api.get('/admin/merchants', { params: { status: 'approved' } }),
        api.get('/admin/unassigned-dispatchers')
      ]);
      
      setMerchants(merchantsRes.data.merchants || []);
      setUnassignedDispatchers(unassignedRes.data.dispatchers || []);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadMerchantDispatchers = async (merchantId: number) => {
    try {
      const response = await api.get(`/admin/merchants/${merchantId}/dispatchers`);
      setAssignedDispatchers(response.data.dispatchers || []);
    } catch (err: any) {
      console.error('Failed to load merchant dispatchers:', err);
    }
  };

  const handleSelectMerchant = async (merchant: Merchant) => {
    setSelectedMerchant(merchant);
    setViewMode('assigned');
    await loadMerchantDispatchers(merchant.id);
  };

  const handleAssignDispatchers = async () => {
    if (!selectedMerchant || selectedDispatchers.length === 0) return;
    
    setAssigning(true);
    setError(null);
    try {
      for (const dispatcherId of selectedDispatchers) {
        await api.post('/admin/assign-dispatcher-to-merchant', {
          merchant_id: selectedMerchant.id,
          dispatcher_id: dispatcherId
        });
      }
      
      setSuccess(`Successfully assigned ${selectedDispatchers.length} dispatcher(s) to ${selectedMerchant.business_name}`);
      setSelectedDispatchers([]);
      setShowAssignModal(false);
      
      await loadData();
      await loadMerchantDispatchers(selectedMerchant.id);
      
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to assign dispatchers');
    } finally {
      setAssigning(false);
    }
  };

  const handleUnassignDispatcher = async (dispatcherId: number) => {
    if (!selectedMerchant) return;
    
    if (!window.confirm('Are you sure you want to unassign this dispatcher from the merchant?')) return;
    
    try {
      await api.delete('/admin/unassign-dispatcher-from-merchant', {
        data: {
          merchant_id: selectedMerchant.id,
          dispatcher_id: dispatcherId
        }
      });
      setSuccess('Dispatcher unassigned successfully');
      
      await loadData();
      await loadMerchantDispatchers(selectedMerchant.id);
      
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to unassign dispatcher');
    }
  };

  const toggleDispatcherSelection = (dispatcherId: number) => {
    setSelectedDispatchers(prev => 
      prev.includes(dispatcherId)
        ? prev.filter(id => id !== dispatcherId)
        : [...prev, dispatcherId]
    );
  };

  const filteredMerchants = merchants.filter(m => 
    m.business_name?.toLowerCase().includes(merchantSearch.toLowerCase()) ||
    m.full_name?.toLowerCase().includes(merchantSearch.toLowerCase()) ||
    m.merchant_code?.toLowerCase().includes(merchantSearch.toLowerCase())
  );

  const filteredDispatchers = (viewMode === 'unassigned' ? unassignedDispatchers : assignedDispatchers)
    .filter(d =>
      d.full_name?.toLowerCase().includes(dispatcherSearch.toLowerCase()) ||
      d.email?.toLowerCase().includes(dispatcherSearch.toLowerCase())
    );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Dispatcher Assignment</h1>
        <p className="mt-2 text-gray-600">Assign dispatchers to merchants or manage existing assignments</p>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg flex items-center gap-2">
          <ExclamationCircleIcon className="h-5 w-5" />
          {error}
          <button onClick={() => setError(null)} className="ml-auto">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
      )}

      {success && (
        <div className="mb-4 bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded-lg flex items-center gap-2">
          <CheckCircleIcon className="h-5 w-5" />
          {success}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 bg-purple-100 rounded-lg">
              <BuildingOfficeIcon className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-600">Total Merchants</p>
              <p className="text-2xl font-bold text-gray-900">{merchants.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 bg-blue-100 rounded-lg">
              <UserGroupIcon className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-600">Unassigned Dispatchers</p>
              <p className="text-2xl font-bold text-gray-900">{unassignedDispatchers.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 bg-green-100 rounded-lg">
              <CheckCircleIcon className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-600">Total Assigned</p>
              <p className="text-2xl font-bold text-gray-900">
                {merchants.reduce((sum, m) => sum + (m.dispatchers_count || 0), 0)}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900 mb-3">Merchants</h2>
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search merchants..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                value={merchantSearch}
                onChange={(e) => setMerchantSearch(e.target.value)}
              />
            </div>
          </div>
          <div className="divide-y divide-gray-200 max-h-96 overflow-y-auto">
            {filteredMerchants.length === 0 ? (
              <div className="p-6 text-center text-gray-500">No merchants found</div>
            ) : (
              filteredMerchants.map((merchant) => (
                <div
                  key={merchant.id}
                  className={`p-4 hover:bg-gray-50 cursor-pointer ${
                    selectedMerchant?.id === merchant.id ? 'bg-primary-50 border-l-4 border-primary-500' : ''
                  }`}
                  onClick={() => handleSelectMerchant(merchant)}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900">{merchant.business_name}</p>
                      <p className="text-sm text-gray-500">{merchant.full_name}</p>
                      <p className="text-xs text-gray-400 font-mono">Code: {merchant.merchant_code}</p>
                    </div>
                    <div className="text-right">
                      <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs">
                        <UserGroupIcon className="h-3 w-3" />
                        {merchant.dispatchers_count || 0}
                      </span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-medium text-gray-900">
                {selectedMerchant ? `${selectedMerchant.business_name} - Dispatchers` : 'Unassigned Dispatchers'}
              </h2>
              {selectedMerchant && (
                <div className="flex gap-2">
                  <button
                    onClick={() => setViewMode('unassigned')}
                    className={`px-3 py-1 text-sm rounded ${
                      viewMode === 'unassigned'
                        ? 'bg-primary-100 text-primary-700'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    Unassigned
                  </button>
                  <button
                    onClick={() => {
                      setViewMode('assigned');
                      loadMerchantDispatchers(selectedMerchant.id);
                    }}
                    className={`px-3 py-1 text-sm rounded ${
                      viewMode === 'assigned'
                        ? 'bg-primary-100 text-primary-700'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    Assigned
                  </button>
                </div>
              )}
            </div>
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search dispatchers..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                value={dispatcherSearch}
                onChange={(e) => setDispatcherSearch(e.target.value)}
              />
            </div>
          </div>

          <div className="divide-y divide-gray-200 max-h-80 overflow-y-auto">
            {filteredDispatchers.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                {viewMode === 'unassigned' ? 'No unassigned dispatchers' : 'No dispatchers assigned to this merchant'}
              </div>
            ) : (
              filteredDispatchers.map((dispatcher) => (
                <div
                  key={dispatcher.id}
                  className={`p-4 hover:bg-gray-50 ${
                    selectedDispatchers.includes(dispatcher.id) ? 'bg-primary-50' : ''
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {viewMode === 'unassigned' && selectedMerchant && (
                        <input
                          type="checkbox"
                          checked={selectedDispatchers.includes(dispatcher.id)}
                          onChange={() => toggleDispatcherSelection(dispatcher.id)}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        />
                      )}
                      <div>
                        <p className="font-medium text-gray-900">{dispatcher.full_name}</p>
                        <p className="text-sm text-gray-500">{dispatcher.email}</p>
                        <div className="flex items-center gap-2 mt-1">
                          {dispatcher.vehicle_type && (
                            <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                              {dispatcher.vehicle_type}
                            </span>
                          )}
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            dispatcher.is_available
                              ? 'bg-green-100 text-green-700'
                              : 'bg-gray-100 text-gray-600'
                          }`}>
                            {dispatcher.is_available ? 'Available' : 'Busy'}
                          </span>
                          {dispatcher.assignment_type && (
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              dispatcher.assignment_type === 'self_registered'
                                ? 'bg-blue-100 text-blue-700'
                                : 'bg-purple-100 text-purple-700'
                            }`}>
                              {dispatcher.assignment_type === 'self_registered' ? 'Self Registered' : 'Admin Assigned'}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    {viewMode === 'assigned' && selectedMerchant && (
                      <button
                        onClick={() => handleUnassignDispatcher(dispatcher.id)}
                        className="text-red-600 hover:text-red-800 text-sm"
                      >
                        Unassign
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          {viewMode === 'unassigned' && selectedMerchant && selectedDispatchers.length > 0 && (
            <div className="p-4 border-t border-gray-200 bg-gray-50">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">
                  {selectedDispatchers.length} dispatcher(s) selected
                </span>
                <button
                  onClick={handleAssignDispatchers}
                  disabled={assigning}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                >
                  <ArrowRightIcon className="h-4 w-4" />
                  {assigning ? 'Assigning...' : `Assign to ${selectedMerchant.business_name}`}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDispatcherAssignment;
